import { apiClient } from '@/lib/api-client';
import type { LatLng } from '@/types/global';
import type { IpLocationResponse } from '../types/location.types';

/**
 * API response format from IP location service
 */
interface IpApiResponse {
  loc?: string; // "lat,lng" format
  country?: string;
  country_name?: string;
  country_code?: string;
  countryCode?: string;
  city?: string;
  timezone?: string;
  time_zone?: string;
  location?: {
    lat?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  };
  lat?: number;
  lng?: number;
  latitude?: number;
  longitude?: number;
}

/**
 * IP-based geolocation API service
 */
export const ipLocationApi = {
  /**
   * Get user's approximate location based on IP address
   * This is used as a smart default location before GPS is enabled
   *
   * @returns Location data with coordinates and optional metadata
   */
  async getLocationByIp(): Promise<IpLocationResponse | null> {
    try {
      const response = await apiClient.get<IpApiResponse>('/api/v1/ip');

      // Handle different possible response formats
      let location: LatLng | null = null;

      // Parse location from "lat,lng" string format (e.g., "25.7763,-80.2919")
      if (response?.loc && typeof response.loc === 'string') {
        const parts = response.loc.split(',');
        if (parts.length === 2) {
          const lat = Number(parts[0]);
          const lng = Number(parts[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            location = { lat, lng };
          }
        }
      }
      // Fallback: Try other possible response formats
      else if (response?.location) {
        const lat = response.location.lat ?? response.location.latitude;
        const lng = response.location.lng ?? response.location.longitude;
        if (lat != null && lng != null) {
          location = { lat, lng };
        }
      } else if (response?.lat != null && response?.lng != null) {
        location = {
          lat: response.lat,
          lng: response.lng,
        };
      } else if (response?.latitude != null && response?.longitude != null) {
        location = {
          lat: response.latitude,
          lng: response.longitude,
        };
      }

      // Extract metadata
      const country = response?.country || response?.country_name;
      const countryCode =
        response?.country_code || response?.countryCode || response?.country;
      const city = response?.city;
      const timezone = response?.timezone || response?.time_zone;

      if (!location) {
        console.warn('IP location API returned no valid coordinates');
        return null;
      }

      return {
        location,
        country,
        countryCode,
        city,
        timezone,
      };
    } catch (error) {
      console.warn('Failed to fetch IP-based location:', error);
      return null;
    }
  },
};
